import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { parsePhoneNumberWithError } from 'libphonenumber-js';

import { ConfigurationService } from '@sc/data-access-configuration';
import { CLIENT_APP_ID, ClientAppId, country } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { PhoneNumberDisplayService } from '@sc/feature-contextual-display';
import { randomId } from '@sc/utils';

import { LabelRequiredPipe } from '../../../pipes';
import { ErrorMessagesService } from '../../../services';
import { UtilsValidators } from '../../../validators';
import { CheckboxComponent } from '../../checkbox';
import { ErrorComponent } from '../../error';
import { FlagComponent } from '../../flag';
import { IconComponent } from '../../icon';
import { SelectComponent } from '../../select';
import { PhoneNumberValidator } from '../validator';

@Component({
  selector: 'sc-phone-input',
  styleUrls: ['./phone-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    NgClass,
    ErrorComponent,
    CheckboxComponent,
    SelectComponent,
    IconComponent,
    FlagComponent,
    MatInputModule,
    LabelRequiredPipe,
    TranslateModule,
    AsyncPipe,
  ],
  template: `
    <mat-label
      *ngIf="label && appearance === 'outline'"
      class="sc-design-text-4"
    >
      <label [for]="labelId">{{ label }}</label>
    </mat-label>

    <mat-form-field
      [appearance]="appearance"
      [ngClass]="{
        error: errorState,
        'with-country-select': countrySelectionVisible
      }"
    >
      <mat-label
        *ngIf="label && appearance !== 'outline'"
        class="sc-design-text-4"
        [innerHTML]="
          label | labelRequired: requiredState:forceHighlightRequired
        "
      >
        {{ label }}
      </mat-label>

      <input
        matInput
        type="tel"
        autocomplete="tel"
        role="textbox"
        class="sc-design-text-3"
        [id]="labelId"
        [value]="value || ''"
        [placeholder]="placeholder"
        [disabled]="disabledState"
        [aria-describedby]="ariaDescribedByValue"
        [attr.data-testid]="dataTestIdValue"
        [attr.title]="inputTitle$ | async"
        [attr.aria-required]="requiredState ? true : undefined"
        [attr.aria-invalid]="errorState ? true : undefined"
        [attr.aria-disabled]="disabledState ? true : undefined"
        (input)="onInputValueChange($event)"
        (blur)="handleBlur()"
        (focusin)="onFocusin()"
        (focusout)="onFocusout()"
      />

      <mat-hint
        *ngIf="
          checkPatternCheckbox && checkPattern && tooltip$ | async as tooltip
        "
        class="sc-design-text-4"
      >
        <p>{{ tooltip }}</p>
      </mat-hint>
    </mat-form-field>

    <sc-error
      *ngIf="displayErrorState"
      [id]="errorId"
      [class.sc-conf-error-on-input-focus]="focus"
      [error]="currentError"
      [displayCondition]="displayErrorState"
    ></sc-error>

    <p class="bloctel sc-design-text-4">
      {{ 'GlobalApplicativeConstants.phoneBloctelInfo' | translate }}
    </p>

    <sc-checkbox
      *ngIf="
        checkPattern &&
        featureFlippingManagerService.isFeatureActivated(
          'can-bypass-phone-validation-with-checkbox'
        )
      "
      id="cb-phone-number-validation"
      class="mt-m"
      [value]="checkPatternCheckbox"
      [disabled]="disabledState"
      (changeValue)="onChangeCheckPattern($event)"
    >
      <span class="sc-design-text-3">
        {{ 'GlobalApplicativeConstants.phoneNumberCheckbox.fr' | translate }}
      </span>
    </sc-checkbox>

    <sc-icon
      *ngIf="errorState"
      class="accessibility-icon error-icon"
      name="alert"
    ></sc-icon>

    <div class="prefix">
      <sc-select
        *ngIf="countrySelectionVisible"
        class="phone-country-select"
        panelClass="phone-country-options-panel"
        [value]="selectedCountry"
        [options]="countryOptions"
        [disabled]="!hasCountrySelect || disabledState"
        (valueChange)="onChangePhoneCountry($event)"
      >
        <ng-template #triggerTemplate let-item>
          <sc-flag [country]="item"></sc-flag>
        </ng-template>

        <ng-template #optionTemplate let-item>
          <sc-flag [country]="item"></sc-flag>
        </ng-template>
      </sc-select>
    </div>
  `,
})
export class PhoneInputComponent
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit
{
  @Input() value = '';
  @Input() disabled = false;
  @Input() clickableIcon = false;
  @Input() forceDisplayError = false;
  @Input() placeholder: string;
  @Input() label: string;
  @Input() required = true;
  @Input() forcedValidators: ValidatorFn[] = [];
  @Input() checkPattern = true;
  @Input() displayCountrySelection = false;
  @Input() forceHighlightRequired: boolean;
  @Input() ariaDescribedby = '';
  @Input() dataTestId?: string;
  @Input() appearance: MatFormFieldAppearance = 'fill';

  @Input()
  set setCountry(cnt: country) {
    if (!cnt || cnt === this.selectedCountry) return;
    this.selectedCountry = cnt;
    this.checkPatternCheckbox =
      this.checkPattern && cnt === this.countryOptions?.[0];
    this.applyValidators();
    this.refreshViewState();
  }

  @Output() changeValue = new EventEmitter<string>();
  @Output() changeCountry = new EventEmitter<country>();
  @Output() rightIconClick = new EventEmitter<string>();

  focus = false;
  checkPatternCheckbox = false;
  countryOptions: country[] = [];
  selectedCountry: country;
  errorId: string;
  labelId: string;

  tooltip$: Observable<string | null> = of(null);
  inputTitle$: Observable<string> = of('');

  disabledState = false;
  requiredState = false;
  errorState = false;
  displayErrorState = false;
  countrySelectionVisible = false;
  hasCountrySelect = false;
  ariaDescribedByValue: string | undefined = undefined;
  dataTestIdValue: string | undefined = undefined;
  currentError: any = null;

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    public errorMessagesService: ErrorMessagesService,
    public configurationService: ConfigurationService,
    private readonly translator: TranslatorService,
    public featureFlippingManagerService: FeatureFlippingManagerService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId,
    private readonly phoneNumberDisplayService: PhoneNumberDisplayService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.countryOptions =
      this.configurationService.getConfig().patterns.phoneNumber || [];
    this.appearance =
      this.configurationService.getConfig()?.themeOptions?.matInputAppearance ||
      'fill';

    this.errorId = randomId(8);
    this.labelId = 'input-text-' + randomId(8);

    if (!this.selectedCountry && this.countryOptions.length > 0) {
      this.selectedCountry = this.countryOptions[0];
    }
  }

  ngOnInit(): void {
    if (!this.selectedCountry && this.countryOptions.length > 0) {
      this.selectedCountry = this.countryOptions[0];
    }

    this.checkPatternCheckbox =
      this.checkPattern && this.selectedCountry === this.countryOptions?.[0];

    this.applyValidators();

    if (this.value) {
      this.value = this.formatPhoneNumber(this.value);
    }

    this.refreshViewState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['required'] ||
      changes['forcedValidators'] ||
      changes['checkPattern'] ||
      changes['displayCountrySelection'] ||
      changes['disabled'] ||
      changes['ariaDescribedby'] ||
      changes['dataTestId'] ||
      changes['label'] ||
      changes['forceDisplayError']
    ) {
      this.applyValidators();
      this.refreshViewState();
    }
  }

  ngAfterViewInit(): void {
    if (this.selectedCountry) {
      this.changeCountry.emit(this.selectedCountry);
    }
  }

  writeValue(value: string): void {
    this.value = this.formatPhoneNumber(value ?? '');
    this.refreshViewState();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.refreshViewState();
  }

  onFocusin(): void {
    this.focus = true;
  }

  onFocusout(): void {
    this.focus = false;
  }

  handleBlur(): void {
    this.onTouched();
    this.refreshViewState();
  }

  onRightIconClick(): void {
    if (this.clickableIcon) {
      this.rightIconClick.emit(this.value);
    }
  }

  onChangePhoneCountry(newPhoneCountry: country): void {
    if (!newPhoneCountry || newPhoneCountry === this.selectedCountry) return;

    this.selectedCountry = newPhoneCountry;
    this.applyValidators();

    this.value = '';
    this.ngControl?.control?.reset('', { emitEvent: false, onlySelf: true });

    this.onChange('');
    this.changeValue.emit('');
    this.changeCountry.emit(newPhoneCountry);

    this.refreshViewState();
  }

  onChangeCheckPattern(event: boolean): void {
    const nextChecked = !!event;
    if (this.checkPatternCheckbox === nextChecked) return;

    this.checkPatternCheckbox = nextChecked;

    if (
      !this.checkPatternCheckbox &&
      this.selectedCountry === this.countryOptions?.[0]
    ) {
      this.applyValidators();
      this.onChange(this.value);
      this.changeValue.emit(this.value);
      this.changeCountry.emit(null);
      this.refreshViewState();
      return;
    }

    if (this.checkPatternCheckbox && this.countryOptions?.length > 0) {
      if (this.selectedCountry !== this.countryOptions[0]) {
        this.selectedCountry = this.countryOptions[0];
        this.changeCountry.emit(this.selectedCountry);
      }
    }

    this.applyValidators();
    this.onChange(this.value);
    this.changeValue.emit(this.value);
    this.refreshViewState();
  }

  onInputValueChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    let rawValue = input.value ?? '';

    if (rawValue.startsWith('00')) {
      rawValue = '+' + rawValue.slice(2);
    }

    this.updateCountryFromInternationalNumber(rawValue);

    const nextValue = this.shouldFormatPhone(rawValue)
      ? this.formatPhoneNumber(rawValue)
      : rawValue;

    if (input.value !== nextValue) {
      input.value = nextValue;
    }

    if (this.value !== nextValue) {
      this.value = nextValue;
      this.onChange(nextValue);
      this.changeValue.emit(nextValue);
    }

    this.refreshViewState();
  }

  private applyValidators(): void {
    const control = this.ngControl?.control;
    if (!control) return;

    control.setValidators(this.getPhoneValidators());
    control.updateValueAndValidity({ emitEvent: false, onlySelf: true });
  }

  private getPhoneValidators(customCountry?: country): ValidatorFn[] {
    const phoneCountry = customCountry ?? this.selectedCountry;

    return [
      ...this.forcedValidators,
      this.required ? Validators.required : Validators.nullValidator,
      Validators.maxLength(100),
      this.checkPatternCheckbox && phoneCountry
        ? PhoneNumberValidator.pattern([phoneCountry])
        : Validators.nullValidator,
    ];
  }

  private formatPhoneNumber(val: string): string {
    if (!val || val.trim() === '') {
      return val;
    }

    try {
      const isInternational = val.trim().startsWith('+');
      const formatted =
        this.phoneNumberDisplayService.getInstantPhoneNumberTranslation(val, {
          isLocal: !isInternational && this.checkPattern,
        });
      const phoneNumber = parsePhoneNumberWithError(val);
      if (
        isInternational &&
        this.countryOptions.includes(phoneNumber.country as any)
      ) {
        const country =
          this.phoneNumberDisplayService.convertLibPhoneNumberCountryToCountryCode(
            phoneNumber.country,
            val
          );
        this.changeCountry.emit(country);
      }
      return formatted || val;
    } catch (error) {
      console.warn('Phone number formatting failed:', error);
      return val;
    }
  }

  private shouldFormatPhone(val: string): boolean {
    if (!val || val.trim() === '') return false;
    if (!this.selectedCountry || !this.checkPatternCheckbox) return false;

    const phoneRegex = /^[\d\s+()\-]+$/;
    return phoneRegex.test(val) && val.length >= 8 && val.length <= 20;
  }

  private updateCountryFromInternationalNumber(val: string): void {
    if (!val || !val.startsWith('+')) return;

    try {
      const parsed = parsePhoneNumberWithError(val);
      const parsedCountry =
        this.phoneNumberDisplayService.convertLibPhoneNumberCountryToCountryCode(
          parsed?.country,
          val
        );

      if (
        parsedCountry &&
        parsedCountry !== this.selectedCountry &&
        this.countryOptions.includes(parsedCountry as any)
      ) {
        this.selectedCountry = parsedCountry;
        this.applyValidators();
        this.changeCountry.emit(parsedCountry);
      }
    } catch {
      // ignore pendant la saisie
    }
  }

  private refreshViewState(): void {
    this.disabledState = !!this.ngControl?.disabled || !!this.disabled;
    this.requiredState = UtilsValidators.hasRequiredValidator(
      this.ngControl?.control
    );
    this.errorState = !!(
      (this.ngControl?.invalid &&
        (this.ngControl?.dirty || this.ngControl?.touched)) ||
      this.forceDisplayError
    );

    this.currentError = this.ngControl?.control
      ? this.errorMessagesService.getError(
          this.ngControl.control,
          this.label,
          'phoneNumberPattern'
        )
      : null;

    this.displayErrorState = !!this.currentError && this.errorState;
    this.countrySelectionVisible =
      this.displayCountrySelection && this.countryOptions.length > 1;
    this.hasCountrySelect = this.countryOptions.length > 1;

    const describerIdList: string[] = [];
    if (this.errorState) describerIdList.push(this.errorId);
    if (this.ariaDescribedby) describerIdList.push(this.ariaDescribedby);
    this.ariaDescribedByValue = describerIdList.join(' ').trim() || undefined;

    this.dataTestIdValue =
      this.dataTestId || (this.ngControl?.name as string) || undefined;

    this.inputTitle$ = this.translator.getTranslation(
      'GlobalApplicativeConstants.accessibility.inputTitle',
      {
        label: this.label,
        isRequired: this.requiredState && !this.disabledState,
        value: this.value || '',
        hasValue: !!this.value,
      }
    );

    this.tooltip$ =
      this.clientAppId === 'customer' && this.selectedCountry
        ? this.translator.getTranslation(
            `GlobalApplicativeConstants.phoneNumberInfoTooltip.${this.selectedCountry}`
          )
        : of(null);
  }
}
